import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const sectionIds = ['showreel', 'about', 'stats', 'vision', 'companies', 'portfolio', 'partners', 'news', 'contact'];

export const SectionNavigator = () => {
  const { t } = useTranslation();
  const [currentSection, setCurrentSection] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const sections = sectionIds.map(id => ({
    id,
    label: t(`sectionNav.${id}`),
  }));

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const heroEl = document.getElementById('hero');
    if (heroEl) {
      const heroObs = new IntersectionObserver(
        ([entry]) => setIsVisible(!entry.isIntersecting),
        { threshold: 0.1 }
      );
      heroObs.observe(heroEl);
      observers.push(heroObs);
    }

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setCurrentSection(id);
        },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const currentIndex = sections.findIndex((s) => s.id === currentSection);

  const scrollTo = useCallback(
    (direction: 'up' | 'down') => {
      const nextIndex =
        direction === 'up'
          ? Math.max(0, currentIndex - 1)
          : Math.min(sections.length - 1, currentIndex + 1);
      const el = document.getElementById(sections[nextIndex].id);
      el?.scrollIntoView({ behavior: 'smooth' });
    },
    [currentIndex, sections]
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="fixed left-6 lg:left-16 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col items-center gap-0"
        >
          <div className="flex flex-col items-center gap-4 mb-0">
            <button
              onClick={() => scrollTo('up')}
              disabled={currentIndex <= 0}
              className="w-10 h-10 rounded-full border border-foreground/30 flex items-center justify-center text-foreground/60 transition-all duration-300 hover:border-primary hover:text-primary hover:shadow-[0_0_15px_hsl(290_46%_49%_/_0.4)] disabled:opacity-30 disabled:hover:border-foreground/30 disabled:hover:text-foreground/60 disabled:hover:shadow-none"
            >
              <ChevronUp size={18} />
            </button>

            <button
              onClick={() => scrollTo('down')}
              disabled={currentIndex >= sections.length - 1}
              className="w-10 h-10 rounded-full border border-foreground/30 flex items-center justify-center text-foreground/60 transition-all duration-300 hover:border-primary hover:text-primary hover:shadow-[0_0_15px_hsl(290_46%_49%_/_0.4)] disabled:opacity-30 disabled:hover:border-foreground/30 disabled:hover:text-foreground/60 disabled:hover:shadow-none"
            >
              <ChevronDown size={18} />
            </button>
          </div>

          <div className="relative flex flex-col items-center">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-primary/40" />
            
            <div className="h-32" />

            <div
              className="relative flex flex-col items-center gap-0 justify-center px-0"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              <span className="text-primary text-[10px] font-semibold tracking-[0.3em] uppercase py-2">
                WE CREATE
              </span>
              <span className="text-foreground/40 text-[10px] font-semibold tracking-[0.3em] uppercase py-2">
                YOU CELEBRATE
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
